/*
 * This file contains the floorplans server component.
 */

import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import FloorplanFilters from "../blocks/floorplanFilters";
import FloorplanHeader from "../blocks/floorplanHeader";
import FloorplanGrid from "../blocks/floorplanGrid";
import FloorplanList from "../blocks/floorplanList";
import fetchData from "../../../utils/fetchData";
import {
  getSortArg,
  getSortOrder,
  getSortValue,
  getSortValueRaw,
} from "../blocks/createFilterOptions";

export default function FloorplansServerComponent({ typeListing }) {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const { floorplanId } = useParams();
  
  // Check if we should restore params from localStorage for breadcrumb navigation
  const isRestoreFromBreadcrumb = sessionStorage.getItem('isBreadcrumbNavigation') === 'true';
  const shouldRestoreFromStorage = typeListing && isRestoreFromBreadcrumb && localStorage.getItem('floorplanTypeListingParams');
  const storedParams = shouldRestoreFromStorage ? new URLSearchParams(localStorage.getItem('floorplanTypeListingParams')) : null;
  const currentParams = storedParams || searchParams;

  // For unit listing, always use "list" view. For floorplan listing, use "grid" view.
  // Ensure activeTab is valid for the current listing type (grid/map for typeListing, list/map for unit listing)
  const getInitialActiveTab = () => {
    const storedTab = localStorage.getItem("activeTab");
    if (typeListing) {
      // Type listing only supports "grid" and "map", default to "grid"
      return storedTab === "grid" || storedTab === "map" ? storedTab : "grid";
    }
    // Unit listing only supports "list" and "map", default to "list"
    return storedTab === "list" || storedTab === "map" ? storedTab : "list";
  };
  const [activeTab, setActiveTab] = useState(getInitialActiveTab);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // This state holds the selected options for the floorplans.
  const [selectedOptions, setSelectedOptions] = useState({
    bed: currentParams.get("bed") || null,
    price: currentParams.get("price") || null,
    sqft: currentParams.get("sqft") || null,
    moveInStartDate: currentParams.get("moveInStartDate") || null,
    sort:
      getSortValue(currentParams.get("sort"), currentParams.get("direction")) ||
      null,
    direction: currentParams.get("direction") || null,
  });

  // This state holds the sort value for the floorplans.
  const [sort, setSort] = useState(
    getSortValue(currentParams.get("sort"), currentParams.get("direction")) ||
      null
  );

  // This state holds the direction value for the floorplans.
  const [direction, setDirection] = useState(
    currentParams.get("direction") || null
  );

  // This state holds the page number for the floorplans.
  const [page, setPage] = useState(parseInt(searchParams.get("page")) || 1);

  // This state holds the page changed value for the floorplans.
  const [pageChanged, setPageChanged] = useState(false);
  // const [pageStatus, setPageStatus] = useState(false);
  const [loading, setLoading] = useState(false);
  const hasRunEffect = useRef(false);
  const [isUserInteraction, setIsUserInteraction] = useState(false);
  const [isPaginationInteraction, setIsPaginationInteraction] = useState(false);
  const [params, setParams] = useState(null);

  // This constant holds the endpoint for the floorplans.
  const endpoint = typeListing
    ? "/wp-json/entrata/v1/getFloorPlan"
    : `/wp-json/entrata/v1/getFloorPlan/${floorplanId}`;

  // This state holds the window width for the floorplans.
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0
  );

  // This effect handles the window resize event.
  // It updates the window width state. For floorplan listing only, sets the active tab to grid if the window width is less than 1240 and the active tab is list.
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);

      // Only switch to grid on resize for floorplan listing, not unit listing
      if (typeListing && windowWidth < 1241 && activeTab === "list") {
        setActiveTab("grid");
      }
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", handleResize);
      }
    };
  }, [windowWidth, activeTab, typeListing]);

  // This effect handles the popstate event.
  // It updates the page state with the new page number and selected options based on the search params.
  useEffect(() => {
    const handlePopState = () => {
      const currentParams = new URLSearchParams(window.location.search);

      // Update page state
      setPage(parseInt(currentParams.get("page")) || 1);

      // Update filter states
      setSelectedOptions((prev) => ({
        ...prev,
        bed: currentParams.get("bed") || null,
        price: currentParams.get("price") || null,
        sqft: currentParams.get("sqft") || null,
        moveInStartDate: currentParams.get("moveInStartDate") || null,
        sort:
          getSortValue(
            currentParams.get("sort"),
            currentParams.get("direction")
          ) || null,
        direction: currentParams.get("direction") || null,
      }));

      setSort(
        getSortValue(
          currentParams.get("sort"),
          currentParams.get("direction")
        ) || null
      );
      setDirection(currentParams.get("direction") || null);

      // Update params state - preserve all query parameters except page
      const paramsList = {};
      for (const [key, value] of currentParams.entries()) {
        if (key !== "page" && value) {
          paramsList[key] = value;
        }
      }
      const paramsUrlForUnitListing = new URLSearchParams(paramsList);
      setParams(paramsUrlForUnitListing.toString());

      // Reset user interaction flag
      setIsUserInteraction(false);

      // Trigger data fetch with current URL parameters
      fetchFloorplans();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // This effect handles the active tab change event.
  // It fetches the floorplans based on the active tab.
  // If the active tab is grid or list, it fetches the floorplans.
  // Otherwise, it navigates to the sightmap page.
  useEffect(() => {
    if (activeTab === "grid" || activeTab === "list") {
      fetchFloorplans();
    } else {
      navigate(`/sightmap`);
    }
    // eslint-disable-next-line
  }, [activeTab]);

  // Store current URL params in localStorage for breadcrumb navigation
  useEffect(() => {
    if (typeListing && window.location.search) {
      localStorage.setItem('floorplanTypeListingParams', window.location.search);
    }
    // Store unit listing params when on unit listing
    if (!typeListing && window.location.search) {
      localStorage.setItem('floorplanUnitListingParams', window.location.search);
    }
    
    // Also store current page for breadcrumb navigation
    if (typeListing && page > 1) {
      localStorage.setItem('floorplanCurrentPage', page.toString());
    } else if (typeListing && page === 1) {
      localStorage.removeItem('floorplanCurrentPage');
    }
    
    // Store unit listing page
    if (!typeListing && page > 1) {
      localStorage.setItem('floorplanUnitCurrentPage', page.toString());
    } else if (!typeListing && page === 1) {
      localStorage.removeItem('floorplanUnitCurrentPage');
    }
  }, [typeListing, window.location.search, page]);

  // Handle breadcrumb restoration with sessionStorage
  useEffect(() => {
    if (isRestoreFromBreadcrumb) {
      // Clear the sessionStorage flag immediately
      sessionStorage.removeItem('isBreadcrumbNavigation');
      
      if (shouldRestoreFromStorage && storedParams) {
        // Restore from localStorage
        const restoredUrl = storedParams.toString();
        navigate(`?${restoredUrl}`, { replace: true });
        localStorage.removeItem('floorplanTypeListingParams');
      }
    }
  }, []); // Empty dependency array to run immediately on mount

  // Clear unit listing params when switching to type listing and vice versa
  useEffect(() => {
    // When switching to type listing, clear unit listing params
    if (typeListing) {
      localStorage.removeItem('floorplanUnitListingParams');
      localStorage.removeItem('floorplanUnitCurrentPage');
    }
    // When switching to unit listing, clear type listing params
    else {
      localStorage.removeItem('floorplanTypeListingParams');
    }
  }, [typeListing]);

  // State for amenities (prefetched with floor plan data)
  const [amenities, setAmenities] = useState(null);
  const [unitAmenities, setUnitAmenities] = useState({});

  const fetchFloorplans = async (type = null) => {
    let filteredOptions = Object.entries(selectedOptions).reduce(
      (acc, [key, value]) => {
        if (value !== null && value !== "" && value !== undefined) {
          acc[key] = value?.toString();
        }
        return acc;
      },
      {}
    );

    filteredOptions.page = type !== null ? 1 : page;

    if (selectedOptions?.sort !== null && selectedOptions?.sort !== "") {
      filteredOptions.sort = getSortArg(sort);
    }

    // Only update URL if this is a user interaction
    if (isUserInteraction && Object.keys(filteredOptions).length) {
      if (!isPaginationInteraction && filteredOptions.page === 1) {
        delete filteredOptions.page;
      }
      // if (filteredOptions.page === 1) delete filteredOptions.page;
      const newUrl = new URLSearchParams(filteredOptions);
      const queryString = newUrl.toString();
      const paramsList = { ...filteredOptions };
      if (!isPaginationInteraction) {
        delete paramsList?.page;
      }
      const paramsUrlForUnitListing = new URLSearchParams(paramsList);
      setParams(paramsUrlForUnitListing?.toString());
      const newUrlString = queryString ? `?${queryString}` : "";
      navigate(newUrlString);
    }

    try {
      setLoading(true);
      const response = await fetchData(`${endpoint}${window.location.search}`);
      setData(response);

      // Extract amenities from main API response (no separate API calls needed)
      if (!typeListing && response?.fp?.[0]) {
        const fp = response.fp[0];
        const units = fp.units || [];
        const amenitiesData = response?.amenities;

        // Handle both formats:
        // 1. Simple array: ["amenity1", "amenity2"] (e.g., for penthouse)
        // 2. Object: { floorplan_only: [...], all_floorplans: [...], unit_only: {...}, ... }
        let floorplanAmenitiesRaw = [];
        let isSimpleArray = Array.isArray(amenitiesData);

        if (isSimpleArray) {
          // Simple array format (penthouse or other simple cases)
          floorplanAmenitiesRaw = amenitiesData || [];
        } else if (amenitiesData && typeof amenitiesData === 'object') {
          // Object format with floorplan_only, all_floorplans, etc.
          floorplanAmenitiesRaw = [
            ...(amenitiesData.floorplan_only || []),
            ...(amenitiesData.all_floorplans || [])
          ];
        }

        // Format: each item becomes { amenities_items: "item1,\nitem2,\n..." }
        const floorplanAmenities = floorplanAmenitiesRaw.length > 0 
          ? [{ amenities_items: floorplanAmenitiesRaw.join(",\n") }]
          : [];
        setAmenities(floorplanAmenities);

        // Process unit-specific amenities from aggregated amenities object only
        // (Only applicable for object format, not simple array)
        const unitAmenitiesMap = {};
        if (!isSimpleArray && amenitiesData && typeof amenitiesData === 'object') {
          units.forEach(unit => {
            const marketingName = unit?.marketing_name;
            const floorNumber = unit?.floor_number;
            
            if (marketingName) {
              const unitAmenityItems = [];

              // Get unit_only amenities
              if (amenitiesData.unit_only?.[marketingName]) {
                unitAmenityItems.push(amenitiesData.unit_only[marketingName]);
              }

              // Get floor_level_only amenities
              if (floorNumber && amenitiesData.floor_level_only?.[floorNumber]) {
                unitAmenityItems.push(amenitiesData.floor_level_only[floorNumber]);
              }

              // Get unit_and_floor_level amenities
              if (floorNumber && amenitiesData.unit_and_floor_level?.[marketingName]?.[floorNumber]) {
                unitAmenityItems.push(amenitiesData.unit_and_floor_level[marketingName][floorNumber]);
              }

              if (unitAmenityItems.length > 0) {
                // Format to match expected structure: { amenities_items: "item1,\nitem2,\n..." }
                unitAmenitiesMap[marketingName] = [{ amenities_items: unitAmenityItems.join(",\n") }];
              }
            }
          });
        }
        setUnitAmenities(unitAmenitiesMap);
      }

      // Update params after successful data fetch - preserve all parameters except page
      const currentParams = new URLSearchParams(window.location.search);
      const paramsList = {};
      for (const [key, value] of currentParams.entries()) {
        if (key !== "page" && value) {
          paramsList[key] = value;
        }
      }
      const paramsUrlForUnitListing = new URLSearchParams(paramsList);
      setParams(paramsUrlForUnitListing.toString());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // This effect fetches the floorplans when the page or pageChanged state changes.
  useEffect(() => {
    fetchFloorplans();
  }, [page, pageChanged]);

  // This effect sets the page state based on the search params.
  // If the user is visitng the page for the first time, it sets the page to the page number in the search params.
  // If the user interacts with the page via filters (sort, direction, etc), it sets the page to 1 and sets the pageChanged state to true.
  useEffect(() => {
    if (!hasRunEffect.current) {
      hasRunEffect.current = true;
      setPage(
        currentParams.has("page") ? parseInt(currentParams.get("page")) : 1
      );
    } else if (isUserInteraction && !isPaginationInteraction) {
      setPage(1);
      setPageChanged(!pageChanged);
    }
  }, [selectedOptions, isUserInteraction]);

  // Handle filter changes after pagination - reset pagination flag only when actual filters change
  const prevSelectedOptionsRef = useRef(selectedOptions);
  useEffect(() => {
    const prevOptions = prevSelectedOptionsRef.current;
    const currentOptions = selectedOptions;

    // Check if actual filter values have changed (not just the object reference)
    const hasFiltersChanged =
      prevOptions.bed !== currentOptions.bed ||
      prevOptions.price !== currentOptions.price ||
      prevOptions.sqft !== currentOptions.sqft ||
      prevOptions.moveInStartDate !== currentOptions.moveInStartDate ||
      prevOptions.sort !== currentOptions.sort ||
      prevOptions.direction !== currentOptions.direction;

    if (hasFiltersChanged && isPaginationInteraction && hasRunEffect.current) {
      // Actual filters have changed after a pagination interaction
      // Reset pagination flag and go to page 1
      setIsPaginationInteraction(false);
      setPage(1);
      setPageChanged((prev) => !prev);
    }

    // Update the ref with current values
    prevSelectedOptionsRef.current = currentOptions;
  }, [selectedOptions, isPaginationInteraction]);

  useEffect(() => {
    if (sort !== null && sort !== "") {
      setDirection(getSortOrder(sort));
      setSelectedOptions((prev) => ({
        ...prev,
        sort: getSortValueRaw(sort),
        direction: getSortOrder(sort),
      }));
    } else {
      setSelectedOptions((prev) => ({
        ...prev,
        sort: null,
        direction: null,
      }));
    }
  }, [sort]);

  // This function handles the tab change event.
  // It updates the active tab state and sets the active tab in localStorage.
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem("activeTab", tab);
  };

  // This function clears the filters.
  // It sets the selected options state to null for all keys.
  // It sets the sort state to null.
  // It sets the direction state to null.
  // It sets the isUserInteraction state to true means this simulates that the user clicked on the clear filters button.
  const clearFilters = () => {
    setSelectedOptions({
      bed: null,
      price: null,
      sqft: null,
      moveInStartDate: null,
      sort: null,
      direction: null,
    });
    setSort(null);
    setDirection(null);
    setIsUserInteraction(true);
    setIsPaginationInteraction(false);
    
    // Clear localStorage when filters are cleared
    localStorage.removeItem('floorplanTypeListingParams');
    localStorage.removeItem('floorplanCurrentPage');
    localStorage.removeItem('floorplanUnitListingParams');
    localStorage.removeItem('floorplanUnitCurrentPage');
    

  };

  return (
    <div id="floor-plan-template" className="grid">
      <FloorplanHeader />
      <FloorplanFilters
        handleTabChange={handleTabChange}
        activeTab={activeTab}
        selectedOptions={selectedOptions}
        setSelectedOptions={setSelectedOptions}
        typeListing={typeListing}
        setPage={setPage}
        sort={sort}
        setSort={setSort}
        direction={direction}
        setIsUserInteraction={setIsUserInteraction}
        setIsPaginationInteraction={setIsPaginationInteraction}
        clearFilters={clearFilters}
        data={data}
      />
      {/* Grid view only for floorplan type listing */}
      {typeListing && (
        <FloorplanGrid
          data={data}
          typeListing={typeListing}
          page={page}
          setPage={setPage}
          loading={loading}
          contactLink={data?.config?.contact_page_url}
          params={params}
          moveInStartDate={selectedOptions?.moveInStartDate}
          setIsUserInteraction={setIsUserInteraction}
          setIsPaginationInteraction={setIsPaginationInteraction}
        />
      )}
      {/* List view only for unit listing */}
      {!typeListing && (
        <FloorplanList
          data={data}
          typeListing={typeListing}
          page={page}
          setPage={setPage}
          loading={loading}
          contactLink={data?.config?.contact_page_url}
          params={params}
          moveInStartDate={selectedOptions?.moveInStartDate}
          setIsUserInteraction={setIsUserInteraction}
          setIsPaginationInteraction={setIsPaginationInteraction}
          amenities={amenities}
          unitAmenities={unitAmenities}
        />
      )}
    </div>
  );
}
